import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import { useIntl } from '../../../../util/reactIntl';

import { BlockContainer } from '../../BlockBuilder';
import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../../SectionBuilder/SectionContainer';
import SectionTitle from '../SectionTitle';
import { MotionSection, fadeUp, viewportOnce } from '../scrollAnimations';

import css from './FeatureMemberClosests.module.css';

/**
 * Featured Member Closets carousel.
 * Rendered when Page Builder sectionId is `feature-member-closets`.
 *
 * Console: title (`Featured Member **Closets**`), description, and blocks
 * with media, eyebrow (text), member name (title), and Explore Closet CTA.
 * Overlay order matches Figma: eyebrow, then name, then CTA.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Array<Object>?} props.blocks
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const FeatureMemberClosests = props => {
  const {
    sectionId,
    className,
    rootClassName,
    title,
    description,
    appearance,
    blocks = [],
    options,
  } = props;

  const intl = useIntl();
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  // Swiper is ESM-only. Staging SSR (Node + webpack-node-externals) cannot
  // require() it, so load it on the client after mount.
  const [swiperLib, setSwiperLib] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([import('swiper/react'), import('swiper/modules'), import('swiper/css')])
      .then(([swiperReact, swiperModules]) => {
        if (!cancelled) {
          setSwiperLib({
            Swiper: swiperReact.Swiper,
            SwiperSlide: swiperReact.SwiperSlide,
            A11y: swiperModules.A11y,
          });
        }
      })
      .catch(err => {
        console.warn('Failed to load Swiper', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasHeaderFields = hasDataInFields([title, description], fieldOptions);
  const hasBlocks = blocks?.length > 0;
  const [swiper, setSwiper] = useState(null);
  const [navState, setNavState] = useState({ isBeginning: true, isEnd: true });

  const updateNav = useCallback(instance => {
    if (!instance) {
      return;
    }
    const next = {
      isBeginning: instance.isBeginning,
      isEnd: instance.isEnd,
    };
    setNavState(prev =>
      prev.isBeginning === next.isBeginning && prev.isEnd === next.isEnd ? prev : next
    );
  }, []);

  const renderBlock = (block, index) => {
    const blockId = block.blockId || `${sectionId}-block-${index + 1}`;
    const hasMedia = hasDataInFields([block.media], fieldOptions);
    const hasCopy = hasDataInFields([block.text, block.title, block.callToAction], fieldOptions);
    return (
      <BlockContainer id={blockId} className={css.card}>
        {hasMedia ? (
          <div className={css.cardMedia}>
            <Field
              data={block.media}
              sizes="(max-width: 767px) 100vw, (max-width: 1023px) 80vw, 50vw"
              options={options}
            />
          </div>
        ) : null}
        {hasCopy ? (
          <div className={css.cardText}>
            <Field data={block.text} options={options} />
            <Field data={block.title} options={options} />
            <Field data={block.callToAction} className={css.shopNow} options={options} />
          </div>
        ) : null}
      </BlockContainer>
    );
  };

  const { Swiper, SwiperSlide, A11y } = swiperLib || {};

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(css.root, className)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <MotionSection>
        <div className={css.inner}>
          {hasHeaderFields ? (
            <motion.header
              className={css.header}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              <SectionTitle data={title} className={css.title} />
              <Field data={description} className={css.description} options={fieldOptions} />
              {hasBlocks ? (
                <div className={css.arrows}>
                  <button
                    type="button"
                    className={css.arrowPrev}
                    disabled={!swiper || navState.isBeginning}
                    onClick={() => swiper?.slidePrev()}
                    aria-label={intl.formatMessage({ id: 'FeatureMemberClosets.previous' })}
                  />
                  <button
                    type="button"
                    className={css.arrowNext}
                    disabled={!swiper || navState.isEnd}
                    onClick={() => swiper?.slideNext()}
                    aria-label={intl.formatMessage({ id: 'FeatureMemberClosets.next' })}
                  />
                </div>
              ) : null}
            </motion.header>
          ) : null}

          {hasBlocks ? (
            <motion.div
              className={css.carousel}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              {Swiper ? (
                <Swiper
                  modules={[A11y]}
                  slidesPerView={1}
                  slidesPerGroup={1}
                  spaceBetween={16}
                  speed={600}
                  grabCursor
                  watchOverflow
                  onSwiper={instance => {
                    setSwiper(instance);
                    updateNav(instance);
                  }}
                  onSlideChange={updateNav}
                  breakpoints={{
                    768: {
                      slidesPerView: 1,
                      slidesPerGroup: 1,
                      spaceBetween: 24,
                    },
                    1025: {
                      slidesPerView: 2,
                      slidesPerGroup: 2,
                      spaceBetween: 40,
                    },
                  }}
                  a11y={{
                    enabled: true,
                    containerMessage: intl.formatMessage({ id: 'FeatureMemberClosets.carousel' }),
                  }}
                >
                  {blocks.map((block, index) => (
                    <SwiperSlide
                      key={`${block.blockId || `${sectionId}-block-${index + 1}`}_i${index}`}
                    >
                      {renderBlock(block, index)}
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <div className={css.card}>{blocks[0] ? renderBlock(blocks[0], 0) : null}</div>
              )}
            </motion.div>
          ) : null}
        </div>
      </MotionSection>
    </SectionContainer>
  );
};

export default FeatureMemberClosests;
